
import { User, getManualPlayerId } from "@/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getGameScoreIds } from "@/integrations/supabase/scores";

interface GameProgressBarsProps {
  golfers: User[];
  courseId: string;
  gameId?: string;
  gameIds?: string[];
  refreshKey?: number;
}

const GameProgressBars = ({
  golfers,
  courseId,
  gameId,
  gameIds,
  refreshKey = 0
}: GameProgressBarsProps) => {
  const [birdieData, setBirdieData] = useState<{[key: string]: number}>({});
  const [loading, setLoading] = useState(false);
  const [totalPossibleBirdies, setTotalPossibleBirdies] = useState(18); // Default to standard 18 holes

  // Fetch birdie data from Supabase
  useEffect(() => {
    const fetchBirdieData = async () => {
      // Determine which game IDs to query
      const targetGameIds = gameIds && gameIds.length > 0 ? gameIds : (gameId ? [gameId] : []);
      if (!courseId || targetGameIds.length === 0) return;

      setLoading(true);

      try {
        // Fetch hole count for the course to set total possible birdies
        const { data: courseHoles, error: courseError } = await supabase
          .from('holes')
          .select('hole_number')
          .eq('course_id', courseId);

        if (courseError) {
          console.error("Error fetching course holes:", courseError);
        } else if (courseHoles) {
          setTotalPossibleBirdies(courseHoles.length || 18);
        }

        // Collect all score IDs across target games
        const allScoreIds: string[] = [];
        for (const gid of targetGameIds) {
          const scoreIds = await getGameScoreIds(gid);
          allScoreIds.push(...scoreIds);
        }
        const uniqueScoreIds = [...new Set(allScoreIds)];

        const newBirdieData: {[key: string]: number} = {};

        for (const golfer of golfers) {
          if (uniqueScoreIds.length === 0) {
            newBirdieData[golfer.id] = 0;
            continue;
          }

          const mpId = getManualPlayerId(golfer.id);
          let scoresQuery = supabase
            .from('scores')
            .select('hole_number')
            .eq('course_id', courseId)
            .in('id', uniqueScoreIds);
          scoresQuery = mpId
            ? scoresQuery.eq('manual_player_id', mpId)
            : scoresQuery.eq('user_id', golfer.id).is('manual_player_id', null);
          const { data: scores, error: scoresError } = await scoresQuery;

          if (scoresError) {
            console.error("Error fetching scores:", scoresError);
            newBirdieData[golfer.id] = 0;
          } else {
            const uniqueHoles = new Set(scores?.map(score => score.hole_number) || []);
            newBirdieData[golfer.id] = uniqueHoles.size;
          }
        }

        setBirdieData(newBirdieData);
      } catch (error) {
        console.error("Error fetching birdie data:", error);
        toast("Error fetching birdie data");
      } finally {
        setLoading(false);
      }
    };

    fetchBirdieData();
  }, [golfers, courseId, gameId, gameIds, refreshKey]);

  return (
    <div className="mb-6">
      <Card className="border-forest/20">
        <CardContent className="p-4">
          <CardTitle className="font-display text-sm font-medium mb-2">Birdie Progress</CardTitle>
          <CardDescription className="text-xs mb-4">
            Track unique holes birdied by each golfer
          </CardDescription>
          
          <div className="space-y-3">
            {golfers.map((golfer) => {
              // Get birdie count from API data
              const birdieCount = birdieData[golfer.id] !== undefined
                ? birdieData[golfer.id]
                : 0;
              
              // Calculate completion percentage
              const completionPercentage = Math.min(
                Math.round((birdieCount / totalPossibleBirdies) * 100),
                100
              );
              
              return (
                <div key={golfer.id} className="space-y-1">
                  <div className="flex justify-between items-center text-xs">
                    <span>{golfer.name}</span>
                    <span className="font-semibold text-gold">
                      {birdieCount} / {totalPossibleBirdies} Holes
                    </span>
                  </div>
                  <Progress 
                    value={completionPercentage} 
                    className="h-2 bg-muted"
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GameProgressBars;
